import { useAdminSecretPanel } from '@/hooks/useAdminSecretPanel';
import { useAuth } from '@/auth/useAuth';
import { CorreccionesStepper } from '@/components/asesorias/CorreccionesStepper';
import { NtcResumenTable } from '@/components/asesorias/NtcResumenTable';
import { NuevaEvidenciaModal } from '@/components/asesorias/NuevaEvidenciaModal';
import { RequiereCorreccionesBanner } from '@/components/asesorias/RequiereCorreccionesBanner';
import { Input, LoadingContainer, Modal, Textarea } from '@/components/ui';
import { QuickActionsSpeedDial } from '@/components/ui/QuickActionsSpeedDial';
import { useAsesoriaById, useAudioProcessingStatus } from '@/hooks/useAsesorias';
import { useAudioFormUrl, useDeleteAudio, useRequestAudioAccess, useRequestFormAccess, useUpdateAudioFecha } from '@/hooks/useAudios';
import '@/pages/admin/admin.css';
import { asesoriasService } from '@/services/asesorias.service';
import { evidenciasService } from '@/services/evidencias.service';
import { dashboardService } from '@/services/dashboard.service';
import { requisitosService, type EvaluacionTernaDTO } from '@/services/requisitos.service';
import { ASESORIA_READONLY_MESSAGE, getAsesoriaEstadoLabel, isAsesoriaEditable, normalizeAsesoriaEstado } from '@/utils/asesoriaStatus';
import { formatDuracionCorta, formatDuration, formatHorasDecimales, horasDeAudios } from '@/utils/formatDuration';
import { renderRecomendacionesExpandible } from '@/utils/renderWithBold';
import { agruparCadenaArchivos } from '@/utils/fileChains';
import { agruparHistorialPorElemento, estaAprobado, fueApelado, tieneRechazoActivo, RevisionEntry } from '@/utils/revisionHistorial';
import type { CorreccionesContext } from '@/services/asesorias.service';
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  ExternalLink,
  FileUp,
  HelpCircle,
  Info,
  Key,
  MoreVertical,
  Pencil,
  Plus,
  Save,
  Trash2,
  Upload,
} from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'N/A';
  }
}

function formatAudioDate(dateStr?: string | null): string {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return 'N/A';
  }
}

// El día + duración identifica al audio de forma más útil para el asesor que su ID interno.
function nombreConversacionAudio(au: { fechaGrabacion: string; duracionSeg: number }): string {
  return `${formatAudioDate(au.fechaGrabacion)} · ${formatDuracionCorta(au.duracionSeg)}`;
}

function formatDateTimeLocal(dateStr?: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function getEstadoChipStyle(estado: string): React.CSSProperties {
  switch (estado) {
    case 'EN_CURSO':
      return { background: 'var(--admin-green-bg)', color: 'var(--admin-green)' };
    case 'EN_REVISION':
      return { background: 'var(--admin-amber-bg)', color: 'var(--admin-amber)' };
    case 'FINALIZADA':
      return { background: 'var(--admin-blue-bg)', color: 'var(--admin-blue)' };
    default:
      return { background: 'var(--admin-surface2)', color: 'var(--admin-text2)' };
  }
}

/** Badge de solo lectura para evidencias/audios en la pestaña Detalle (sin controles de acción). */
function EstadoBadgeDetalle({ estado, entries = [] }: { estado?: string | null; entries?: RevisionEntry[] }) {
  let bg = 'var(--admin-surface2)';
  let color = 'var(--admin-text2)';
  let label = 'Pendiente';

  const apelado = fueApelado(entries);
  const aprobado = estado === 'APROBADO' || estaAprobado(entries);
  const rechazado = estado === 'RECHAZADO' || tieneRechazoActivo(entries);

  if (aprobado) {
    bg = '#f0fdf4';
    color = '#166534';
    label = 'Aprobado';
  } else if (apelado) {
    bg = '#fef3c7';
    color = '#b45309';
    label = 'Respuesta enviada';
  } else if (rechazado) {
    bg = '#fdf0ee';
    color = '#c0392b';
    label = 'Rechazado';
  }

  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0"
      style={{ background: bg, color }}
    >
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
      {label}
    </span>
  );
}

function getReunionChipStyle(estado: 'PROGRAMADA' | 'COMPLETADA' | 'CANCELADA'): React.CSSProperties {
  switch (estado) {
    case 'PROGRAMADA':
      return { background: 'var(--admin-blue-bg)', color: 'var(--admin-blue)' };
    case 'COMPLETADA':
      return { background: 'var(--admin-green-bg)', color: 'var(--admin-green)' };
    case 'CANCELADA':
      return { background: 'var(--admin-red-bg)', color: 'var(--admin-red)' };
    default:
      return { background: 'var(--admin-surface2)', color: 'var(--admin-text2)' };
  }
}

type AsesoriaTab = 'detalle' | 'correcciones';

// Estados de la asesoría para los cuales existe historial de revisión relevante para Detalle.
const HISTORIAL_ESTADOS_CON_REVISION = new Set(['REQUIERE_CORRECCIONES', 'EN_REVISION', 'REVERTIDA']);

export function AsesoriaDetallePage() {
  const { nit, asesoriaId } = useParams<{ nit: string; asesoriaId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: asesoria, isLoading, error, refetch } = useAsesoriaById(nit ?? '', asesoriaId);
  const { data: data, isLoading: isFormUrlLoading, error: formUrlError } = useAudioFormUrl(nit ?? '');
  const { updateFecha: updateAudioFecha, isUpdating: isAudioFechaUpdating } = useUpdateAudioFecha(nit ?? '', () => refetch());
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as AsesoriaTab) ?? 'detalle';
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [objetivoValue, setObjetivoValue] = useState('');
  const [fechaInicioValue, setFechaInicioValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isRevertiendo, setIsRevertiendo] = useState(false);
  const [isCheckingAudioStatus, setIsCheckingAudioStatus] = useState(false);
  const [showCancelarModal, setShowCancelarModal] = useState(false);
  const [showAudioProcessingWarningModal, setShowAudioProcessingWarningModal] = useState(false);
  const [audioStatusValidationError, setAudioStatusValidationError] = useState<string | null>(null);
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
  const [editingAudioId, setEditingAudioId] = useState<string | null>(null);
  const [editingAudioFecha, setEditingAudioFecha] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingAudioId, setDeletingAudioId] = useState<number | null>(null);
  const [audiosExpanded, setAudiosExpanded] = useState(false);
  const { deleteAudio, isDeleting } = useDeleteAudio(nit ?? '', () => {
    setIsDeleteModalOpen(false);
    setDeletingAudioId(null);
    refetch();
  });
  const {
    requestAccess,
    isRequesting,
    error: accessError,
    successMessage: accessSuccess,
    clearStatus: clearAccessStatus,
  } = useRequestAudioAccess(nit ?? '');
  const {
    requestAccess: requestFormAccess,
    isRequesting: isRequestingFormAccess,
    error: formAccessError,
    successMessage: formAccessSuccess,
    clearStatus: clearFormAccessStatus,
  } = useRequestFormAccess(nit ?? '');
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [accessEmail, setAccessEmail] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const [asesoriasActivas, setAsesoriasActivas] = useState<Array<{ asesoriaId: number; orden: number | null; estado: string }>>([]);
  const [settingFoco, setSettingFoco] = useState(false);
  const showSecretPanel = useAdminSecretPanel();
  const [showFocoExpl, setShowFocoExpl] = useState(false);
  const [historialRevisiones, setHistorialRevisiones] = useState<any[]>([]);
  const [evaluacionesTernas, setEvaluacionesTernas] = useState<EvaluacionTernaDTO[]>([]);
  const [cambioFieldLogs, setCambioFieldLogs] = useState<import('@/types/evaluacionAdmin').CambioFieldLog[]>([]);
  const [ternas, setTernas] = useState<any[]>([]);
  const [requisitos, setRequisitos] = useState<any[]>([]);
  const [ntcs, setNtcs] = useState<any[]>([]);
  const [loadingTernas, setLoadingTernas] = useState(true);

  const fetchTernas = async () => {
    if (!nit || !asesoriaId) return;
    setLoadingTernas(true);
    try {
      const res: any = await requisitosService.getEvaluacion(nit, Number(asesoriaId));
      if (res.success) {
        setEvaluacionesTernas(res.evaluacionesTernas || []);
        setCambioFieldLogs(res.cambioFieldLogs || []);
        setTernas(res.ternas || []);
        setRequisitos(res.requisitos || []);
        console.log({ response: res });
        setNtcs(res.combinacion.ntcs || []);
      }
    } catch (err: any) {
      console.error('Error al cargar evaluaciones de terna:', err);
    } finally {
      setLoadingTernas(false);
    }
  };

  useEffect(() => {
    fetchTernas();
  }, [nit, asesoriaId]);

  const [correccionesCtx, setCorreccionesCtx] = useState<CorreccionesContext | null>(null);

  const fetchCorreccionesContext = useCallback(async () => {
    if (!nit || !asesoriaId) return;
    try {
      const res = await asesoriasService.getCorreccionesContext(nit, Number(asesoriaId));
      setCorreccionesCtx(res);
    } catch (e) {
      console.error('Error fetching correcciones context:', e);
    }
  }, [nit, asesoriaId]);

  const refetchHistorial = useCallback(async () => {
    if (!nit || !asesoriaId || !asesoria) return;
    const estado = normalizeAsesoriaEstado({ estado: asesoria.estado, autoAprobar: asesoria.autoAprobar });
    // Detalle muestra badges de estado que requieren historial también en EN_REVISION y REVERTIDA,
    // no solo en REQUIERE_CORRECCIONES. En EN_CURSO todavía no existe revisión, no se consulta.
    if (HISTORIAL_ESTADOS_CON_REVISION.has(estado)) {
      try {
        const res = await dashboardService.getRevisionHistorial(nit, Number(asesoriaId));
        if (res.success) setHistorialRevisiones(res.historial);
      } catch (err) {
        console.error('Error al cargar historial de revisiones para correcciones:', err);
      }
    }
  }, [nit, asesoriaId, asesoria]);

  const handleResponded = useCallback(async () => {
    await Promise.all([fetchCorreccionesContext(), refetchHistorial()]);
  }, [fetchCorreccionesContext, refetchHistorial]);

  useEffect(() => {
    refetchHistorial();
  }, [refetchHistorial]);

  useEffect(() => {
    fetchCorreccionesContext();
  }, [fetchCorreccionesContext, historialRevisiones]);

  // Ronda de revisión actual de la asesoría (la mayor del historial).
  const rondaActual = correccionesCtx?.rondaActual ?? 1;

  // Elementos que siguen sin aprobación del admin en la ronda actual — incluye tanto los que
  // el asesor todavía debe atender como los que ya respondió y esperan revisión, para que el
  // hilo no desaparezca de la vista apenas se apela (solo desaparece cuando el admin aprueba).
  const elementosRechazados = useMemo(() => {
    if (!correccionesCtx) return [];
    const out: Array<{
      id: number;
      elementoTipo: string;
      elementoId: string;
      elementoNombre?: string;
      observacion?: string;
    }> = [];

    const CAMPOS_TERNA = [
      { tipo: 'OBSERVACIONES', campo: 'observaciones' },
      { tipo: 'PLANMEJORA', campo: 'planMejora' },
      { tipo: 'FECHAIMPLEMENTACION', campo: 'fechaImplementacion' },
    ] as const;

    for (const terna of correccionesCtx.ternasConCorreccion) {
      const etIdStr = String(terna.evaluacionTernaId);
      for (const { tipo, campo } of CAMPOS_TERNA) {
        const estadoCampo = terna.campos[campo];
        if (!estadoCampo.rechazado && !estadoCampo.yaCorregido) continue;
        let label = '';
        if (tipo === 'OBSERVACIONES') label = 'Observaciones';
        else if (tipo === 'PLANMEJORA') label = 'Plan de mejora';
        else if (tipo === 'FECHAIMPLEMENTACION') label = 'Fecha de implementación';

        out.push({
          id: terna.evaluacionTernaId,
          elementoTipo: tipo,
          elementoId: etIdStr,
          elementoNombre: terna.grupo ? `${label} (Grupo: ${terna.grupo})` : label,
          observacion: estadoCampo.comentarioAdmin ?? undefined,
        });
      }

      // Loop over celdas (requirements)
      for (const cell of terna.celdas) {
        if (cell.evaluacionRequisitoId && cell.campos && (cell.campos.rechazado || cell.campos.yaCorregido)) {
          out.push({
            id: cell.evaluacionRequisitoId,
            elementoTipo: 'REQUISITO',
            elementoId: String(cell.evaluacionRequisitoId),
            elementoNombre: cell.numeral ? `Requisito Numeral ${cell.numeral}` : `Requisito ID: ${cell.requisitoId}`,
            observacion: cell.campos.comentarioAdmin ?? undefined,
          });
        }
      }
    }

    for (const ev of correccionesCtx.evidenciasRechazadas) {
      out.push({
        id: ev.evidenciaId,
        elementoTipo: 'EVIDENCIA',
        elementoId: String(ev.evidenciaId),
        elementoNombre: ev.nombre,
        observacion: ev.comentarioAdmin ?? undefined,
      });
    }

    for (const au of correccionesCtx.audiosRechazados) {
      out.push({
        id: au.audioId,
        elementoTipo: 'AUDIO',
        elementoId: String(au.audioId),
        elementoNombre: nombreConversacionAudio(au),
        observacion: au.comentarioAdmin ?? undefined,
      });
    }

    return out;
  }, [correccionesCtx]);

  // Mapa de historial por elemento, para mostrar el último rechazo + histórico inline.
  const historialPorElemento = useMemo(() => agruparHistorialPorElemento(historialRevisiones), [historialRevisiones]);

  // Elementos rechazados que ya fueron corregidos re-subiendo (existe un reemplazo que los apunta).
  const clavesResueltasPorReemplazo = useMemo(() => {
    const s = new Set<string>();
    if (!correccionesCtx) return s;
    for (const ev of correccionesCtx.evidenciasRechazadas) {
      if (ev.yaCorregido && ev.reemplazoEvidenciaId != null) {
        s.add(`EVIDENCIA:${ev.reemplazoEvidenciaId}`);
      }
    }
    for (const au of correccionesCtx.audiosRechazados) {
      if (au.yaCorregido && au.reemplazoAudioId != null) {
        s.add(`AUDIO:${au.reemplazoAudioId}`);
      }
    }
    return s;
  }, [correccionesCtx]);

  const cadenasEvidencias = useMemo(() => {
    if (!asesoria?.evidencias) return [];
    return agruparCadenaArchivos(
      asesoria.evidencias,
      (e) => e.evidenciaId,
      (e) => (e as any).reemplazaEvidenciaId ?? null
    );
  }, [asesoria?.evidencias]);

  const cadenasAudios = useMemo(() => {
    if (!asesoria?.audios) return [];
    return agruparCadenaArchivos(
      asesoria.audios,
      (a) => a.audioId || 0,
      (a) => (a as any).reemplazaAudioId ?? null
    );
  }, [asesoria?.audios]);

  // Rechazados que todavía NO se resolvieron. Bloquean el envío a revisión.
  const correccionesPendientes = useMemo(() => {
    if (!correccionesCtx) return [];
    const out: any[] = [];

    for (const terna of correccionesCtx.ternasConCorreccion) {
      const etIdStr = String(terna.evaluacionTernaId);
      if (terna.campos.observaciones.rechazado && !terna.campos.observaciones.yaCorregido) {
        out.push({
          id: terna.evaluacionTernaId,
          elementoTipo: 'OBSERVACIONES',
          elementoId: etIdStr,
          elementoNombre: terna.grupo ? `Observaciones (Grupo: ${terna.grupo})` : 'Observaciones',
          observacion: terna.campos.observaciones.comentarioAdmin ?? undefined,
        });
      }
      if (terna.campos.planMejora.rechazado && !terna.campos.planMejora.yaCorregido) {
        out.push({
          id: terna.evaluacionTernaId,
          elementoTipo: 'PLANMEJORA',
          elementoId: etIdStr,
          elementoNombre: terna.grupo ? `Plan de mejora (Grupo: ${terna.grupo})` : 'Plan de mejora',
          observacion: terna.campos.planMejora.comentarioAdmin ?? undefined,
        });
      }
      if (terna.campos.fechaImplementacion.rechazado && !terna.campos.fechaImplementacion.yaCorregido) {
        out.push({
          id: terna.evaluacionTernaId,
          elementoTipo: 'FECHAIMPLEMENTACION',
          elementoId: etIdStr,
          elementoNombre: terna.grupo ? `Fecha de implementación (Grupo: ${terna.grupo})` : 'Fecha de implementación',
          observacion: terna.campos.fechaImplementacion.comentarioAdmin ?? undefined,
        });
      }

      // Loop over celdas (requirements)
      for (const cell of terna.celdas) {
        if (cell.evaluacionRequisitoId && cell.campos && cell.campos.rechazado && !cell.campos.yaCorregido) {
          out.push({
            id: cell.evaluacionRequisitoId,
            elementoTipo: 'REQUISITO',
            elementoId: String(cell.evaluacionRequisitoId),
            elementoNombre: cell.numeral ? `Requisito Numeral ${cell.numeral}` : `Requisito ID: ${cell.requisitoId}`,
            observacion: cell.campos.comentarioAdmin ?? undefined,
          });
        }
      }
    }

    for (const ev of correccionesCtx.evidenciasRechazadas) {
      if (!ev.yaCorregido) {
        out.push({
          id: ev.evidenciaId,
          elementoTipo: 'EVIDENCIA',
          elementoId: String(ev.evidenciaId),
          elementoNombre: ev.nombre,
          observacion: ev.comentarioAdmin ?? undefined,
        });
      }
    }

    for (const au of correccionesCtx.audiosRechazados) {
      if (!au.yaCorregido) {
        out.push({
          id: au.audioId,
          elementoTipo: 'AUDIO',
          elementoId: String(au.audioId),
          elementoNombre: nombreConversacionAudio(au),
          observacion: au.comentarioAdmin ?? undefined,
        });
      }
    }

    // Un elemento apelado, corregido o aprobado ya está resuelto para efectos de reenviar a revisión.
    return out.filter((el) => {
      let entries: RevisionEntry[] = [];
      if (el.elementoTipo === 'EVIDENCIA') {
        const idNum = Number(el.elementoId);
        const cadena = cadenasEvidencias.find((c) => c.version.some((v) => v.evidenciaId === idNum));
        if (cadena) {
          entries = cadena.version.flatMap((v) => historialPorElemento.get(`EVIDENCIA:${v.evidenciaId}`) ?? []);
        } else {
          entries = historialPorElemento.get(`EVIDENCIA:${el.elementoId}`) ?? [];
        }
      } else if (el.elementoTipo === 'AUDIO') {
        const idNum = Number(el.elementoId);
        const cadena = cadenasAudios.find((c) => c.version.some((v) => v.audioId === idNum));
        if (cadena) {
          entries = cadena.version.flatMap((v) => historialPorElemento.get(`AUDIO:${v.audioId}`) ?? []);
        } else {
          entries = historialPorElemento.get(`AUDIO:${el.elementoId}`) ?? [];
        }
      } else {
        entries = historialPorElemento.get(`${el.elementoTipo}:${el.elementoId}`) ?? [];
      }
      return !fueApelado(entries) && !estaAprobado(entries);
    });
  }, [correccionesCtx, historialPorElemento, cadenasEvidencias, cadenasAudios]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!nit) return;
    dashboardService
      .getAsesoriasActivas(nit)
      .then((res) => {
        if (res.success) setAsesoriasActivas(res.asesorias.map((a) => ({ asesoriaId: a.asesoriaId, orden: a.orden, estado: a.estado })));
      })
      .catch(() => {});
  }, [nit]);

  const {
    data: audioProcessingStatus,
    error: audioProcessingStatusError,
    isNotFound: isAudioProcessingStatusNotFound,
    refetch: refetchAudioProcessingStatus,
  } = useAudioProcessingStatus(nit ?? '', false);

  // Hooks must run unconditionally — keep them above the early returns below.
  // REQUIERE_CORRECCIONES no bloquea el envío de asesorías posteriores: esa asesoría anterior ya
  // fue enviada y está en manos del asesor para corregirla a su propio ritmo, no impide avanzar con
  // las demás. Solo EN_CURSO (aún sin enviar) o REVERTIDA (devuelta por el admin) bloquean, y cada
  // motivo amerita su propio mensaje.
  const editableAsesorias = useMemo(() => {
    return asesoriasActivas
      .filter((a) => ['EN_CURSO', 'REVERTIDA'].includes(a.estado))
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0) || a.asesoriaId - b.asesoriaId);
  }, [asesoriasActivas]);

  // Solo bloquea una asesoría EN_CURSO/REVERTIDA que sea REALMENTE anterior (orden menor) a esta —
  // no cualquiera de la lista. Antes se comparaba contra `editableAsesorias[0]` sin importar el
  // orden, así que una asesoría #7 recién creada (EN_CURSO, nunca tocada) bloqueaba el envío de la
  // #6 que el asesor sí estaba corrigiendo, aunque la #7 fuera POSTERIOR.
  const asesoriaBloqueante = useMemo(() => {
    if (!asesoria) return undefined;
    const ordenActual = asesoria.orden ?? asesoria.asesoriaId;
    return editableAsesorias.find((a) => (a.orden ?? a.asesoriaId) < ordenActual);
  }, [editableAsesorias, asesoria]);

  const esPrimeraEditable = !asesoriaBloqueante;

  // Cada reemplazo de evidencia es un borrado lógico: la fila vieja no se borra, la nueva apunta a
  // ella vía reemplazaEvidenciaId. Sin agrupar por cadena, cada versión (rechazada, corregida, etc.)
  // aparecía como una tarjeta separada aunque sea "la misma" evidencia — mismo criterio que usa el
  // admin (agruparCadenaArchivos), solo que aquí el asesor únicamente ve la versión VIGENTE (actual).
  const evidenciasVigentes = useMemo(
    () =>
      agruparCadenaArchivos(
        (asesoria?.evidencias ?? []).filter((e) => e.estado !== 'ELIMINADO'),
        (e) => e.evidenciaId,
        (e) => e.reemplazaEvidenciaId ?? null
      ).map((c) => c.actual),
    [asesoria?.evidencias]
  );

  // Ambas condiciones de bloqueo de "Enviar a revisión" se combinan en una sola expresión;
  // si hay correcciones pendientes, ese motivo tiene precedencia sobre el resto.
  const bloqueoCorrecciones = correccionesPendientes.length > 0;
  const disabledEnviarRevision = isCheckingAudioStatus || !esPrimeraEditable || bloqueoCorrecciones;
  const motivoEnviarRevision = bloqueoCorrecciones
    ? 'Debes completar todas las correcciones antes de enviar nuevamente la asesoría.'
    : asesoriaBloqueante?.estado === 'REVERTIDA'
      ? 'Hay una asesoría anterior que requiere correcciones. Debes completarlas antes de enviar esta a revisión.'
      : asesoriaBloqueante
        ? 'Debe finalizar las asesorías anteriores antes de enviar esta a revisión.'
        : undefined;

  if (isLoading) {
    return <LoadingContainer text="Cargando asesoría..." />;
  }

  if (error || !asesoria) {
    return (
      <div className="min-h-screen bg-[#f8f7f4]">
        <div className="max-w-5xl mx-auto px-6 py-6 text-center">
          <h2 className="text-xl font-semibold" style={{ color: 'var(--admin-text)' }}>
            {error || 'Asesoría no encontrada'}
          </h2>
          <Link to="/">
            <button type="button" className="admin-btn primary mt-4">
              Volver al inicio
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const reuniones = asesoria.reuniones.length;
  const isFormUrlReady = Boolean(data) && !isFormUrlLoading && !formUrlError;
  const audioItems = asesoria.audios ?? [];

  const estadoAsesoria = normalizeAsesoriaEstado({ estado: asesoria.estado, autoAprobar: asesoria.autoAprobar });
  const enRevision = estadoAsesoria === 'EN_REVISION';
  const puedeEditar = isAsesoriaEditable(estadoAsesoria);
  // En correcciones, el admin pudo prohibir subir evidencias NUEVAS (solo reemplazo). El
  // reemplazo se hace desde la pestaña de correcciones, no por este botón general.
  const bloqueaSubidaNueva = estadoAsesoria === 'REQUIERE_CORRECCIONES' && asesoria.permiteSubirEvidenciasNuevas === false;
  const puedeSubirEvidencia = puedeEditar && !bloqueaSubidaNueva;
  // Objetivo y fecha solo editables en EN_CURSO y REQUIERE_CORRECCIONES, no en REVERTIDA
  const puedeEditarMetadata = estadoAsesoria === 'EN_CURSO' || estadoAsesoria === 'REQUIERE_CORRECCIONES';
  // Audio focus: this asesoría receives uploads only when it's the active one
  const hasFocus = asesoria.empresa.asesoriaActivaId === asesoria.asesoriaId;

  const getAsesoriaLabel = (id: number) => {
    const a = asesoriasActivas.find((x) => x.asesoriaId === id);
    if (!a) return `#${id}`;
    if (a.orden === 1) return 'Diagnóstico';
    return a.orden != null ? `Asesoría #${a.orden - 1}` : `#${id}`;
  };

  const focusId = asesoria.empresa.asesoriaActivaId ?? null;
  const focusLabel = focusId ? getAsesoriaLabel(focusId) : null;
  const currentOrden = asesoriasActivas.find((x) => x.asesoriaId === asesoria.asesoriaId)?.orden ?? null;
  const focusOrden = focusId ? (asesoriasActivas.find((x) => x.asesoriaId === focusId)?.orden ?? null) : null;
  const focusIsAnterior = currentOrden != null && focusOrden != null && focusOrden === currentOrden - 1;
  const focusIsSiguiente = currentOrden != null && focusOrden != null && focusOrden === currentOrden + 1;

  const handleSetFoco = async () => {
    if (!nit || settingFoco) return;
    setSettingFoco(true);
    try {
      await asesoriasService.setEnFoco(nit, asesoria.asesoriaId);
      await refetch();
    } finally {
      setSettingFoco(false);
    }
  };

  const handleRevertir = async () => {
    if (!nit || !asesoriaId) return;
    setIsRevertiendo(true);
    try {
      await asesoriasService.revertir(nit, Number(asesoriaId));
      setShowCancelarModal(false);
      navigate(`/empresa/${nit}`);
    } catch (err) {
      console.error('Error al revertir asesoría:', err);
    } finally {
      setIsRevertiendo(false);
    }
  };

  const openEditModal = () => {
    setObjetivoValue(asesoria.objetivo ?? '');
    setFechaInicioValue(formatDateTimeLocal(asesoria.fechaInicio));
    setSaveError(null);
    setIsEditOpen(true);
  };

  const closeEditModal = () => {
    if (isSaving) return;
    setIsEditOpen(false);
  };

  const handleSaveChanges = async () => {
    if (!nit) return;
    if (!fechaInicioValue) {
      setSaveError('La fecha de inicio es obligatoria.');
      return;
    }
    const fechaInicioDate = new Date(fechaInicioValue);
    if (Number.isNaN(fechaInicioDate.getTime())) {
      setSaveError('La fecha de inicio no es válida.');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      await asesoriasService.actualizarActiva(nit, objetivoValue.trim(), fechaInicioDate.toISOString());
      await refetch();
      setIsEditOpen(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'No se pudo actualizar la asesoría.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEliminarEvidencia = async (evidenciaId: number, tieneHistorial: boolean) => {
    const msg = tieneHistorial
      ? 'Esta evidencia es un reemplazo de una versión rechazada anteriormente. Al eliminarla, el requisito volverá a quedar marcado como pendiente de corregir. ¿Estás seguro de que deseas eliminarla?'
      : '¿Estás seguro de que deseas eliminar esta evidencia?';

    if (!window.confirm(msg)) return;

    try {
      await evidenciasService.eliminar(nit!, evidenciaId);
      refetch();
    } catch (err) {
      console.error('Error al eliminar evidencia:', err);
      alert('Error al eliminar la evidencia.');
    }
  };

  const handleGoToFinalizar = async () => {
    if (!nit) return;

    setAudioStatusValidationError(null);
    setIsCheckingAudioStatus(true);

    try {
      const latestStatus = await refetchAudioProcessingStatus();

      if (latestStatus && latestStatus.hasAudiosEnProcesamiento) {
        setShowAudioProcessingWarningModal(true);
        return;
      }

      navigate(`/empresa/${nit}/asesoria/${asesoriaId}/finalizar`);
    } catch {
      // Si la validación de audio falla por permisos de red o asesoría activa no registrada en job, permitir continuar a finalizar
      navigate(`/empresa/${nit}/asesoria/${asesoriaId}/finalizar`);
    } finally {
      setIsCheckingAudioStatus(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-[#f8f7f4]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Back button */}
          <button
            type="button"
            onClick={() => navigate(`/empresa/${nit}`)}
            className="flex items-center gap-1.5 text-[12px] text-[#999] hover:text-[#1a1a1a] mb-4 transition-colors"
          >
            ← Volver a empresa
          </button>

          {/* Tab bar */}
          <div className="flex gap-0.5 border-b border-[#ede9e3] mb-4">
            {(['detalle', 'correcciones'] as AsesoriaTab[])
              .filter((tab) => tab !== 'correcciones' || estadoAsesoria === 'REQUIERE_CORRECCIONES')
              .map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.set('tab', tab);
                    next.delete('subtab');
                    setSearchParams(next, { replace: true });
                  }}
                  className={`admin-tab inline-flex items-center gap-1.5 ${activeTab === tab ? 'active' : ''}`}
                >
                  {tab === 'detalle' ? 'Detalle' : 'Correcciones necesarias'}
                  {tab === 'correcciones' && correccionesPendientes.length > 0 && (
                    <span
                      className="inline-flex items-center justify-center rounded-full text-white text-[10px] font-semibold"
                      style={{ minWidth: 17, height: 17, padding: '0 4px', background: '#c0392b' }}
                    >
                      {correccionesPendientes.length}
                    </span>
                  )}
                </button>
              ))}
          </div>

          {activeTab === 'correcciones' && (
            <div className="space-y-6">
              <CorreccionesStepper
                elementosRechazados={elementosRechazados}
                nit={nit ?? ''}
                asesoriaId={Number(asesoriaId) || 0}
                historialPorElemento={historialPorElemento}
                evidencias={asesoria.evidencias || []}
                audios={asesoria.audios || []}
                requisitos={requisitos}
                ternas={ternas}
                evaluacionesTernas={evaluacionesTernas}
                cambioFieldLogs={cambioFieldLogs}
                ntcs={ntcs}
                refetch={async () => {
                  await refetch();
                  await Promise.all([refetchHistorial(), fetchTernas()]);
                }}
                clavesResueltas={clavesResueltasPorReemplazo}
                onResponded={handleResponded}
                permiteSubirEvidenciasNuevas={asesoria?.permiteSubirEvidenciasNuevas}
                correccionesCtx={correccionesCtx}
                onEliminarEvidencia={(id) => handleEliminarEvidencia(id, true)}
                onEliminarAudio={(id) => deleteAudio(id)}
              />
            </div>
          )}

          {activeTab === 'detalle' && (
            <>
              {/* Banner "Requiere correcciones" — primer bloque de la pestaña Detalle */}
              <RequiereCorreccionesBanner estadoAsesoria={estadoAsesoria} />

              {/* Header Asesoría */}
              <div className="admin-detail-panel">
                {/* Identity row */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="admin-avatar" style={{ background: 'var(--admin-surface2)', color: 'var(--admin-text2)' }}>
                    {getInitials(asesoria.empresa.nombre)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-semibold leading-tight truncate" style={{ color: 'var(--admin-text)' }}>
                      {asesoria.empresa.nombre}
                    </p>
                    <p className="text-[12px] flex items-center gap-2" style={{ color: 'var(--admin-text3)' }}>
                      <span>NIT {asesoria.empresa.nit}</span>
                      {showSecretPanel && (
                        <span className="font-mono text-[10px] text-amber-700 bg-amber-50 px-1 py-0.5 rounded border border-amber-200 select-all font-semibold">
                          AsesoriaId: #{asesoria.asesoriaId}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="admin-chip" style={getEstadoChipStyle(estadoAsesoria)}>
                      {getAsesoriaEstadoLabel(estadoAsesoria)}
                    </span>
                    {puedeEditarMetadata && (
                      <button type="button" onClick={openEditModal} className="admin-btn" style={{ fontSize: '11px', padding: '5px 10px' }}>
                        <Pencil className="w-3 h-3" /> Editar
                      </button>
                    )}
                  </div>
                </div>

                {/* Detail grid */}
                <div className="admin-detail-grid">
                  {asesoria.asesor?.nombre && (
                    <div className="admin-detail-field">
                      <p className="text-[10px] uppercase font-medium mb-1" style={{ color: 'var(--admin-text3)', letterSpacing: '0.5px' }}>
                        Asesor
                      </p>
                      <p className="text-[13px] font-semibold" style={{ color: '#1e5fa8' }}>
                        {asesoria.asesor.nombre}
                      </p>
                    </div>
                  )}
                  <div className="admin-detail-field">
                    <p className="text-[10px] uppercase font-medium mb-1" style={{ color: 'var(--admin-text3)', letterSpacing: '0.5px' }}>
                      Inicio
                    </p>
                    <p className="text-[13px] font-medium" style={{ color: 'var(--admin-text)' }}>
                      {formatDateTime(asesoria.fechaInicio)}
                    </p>
                  </div>
                  <div className="admin-detail-field">
                    <p className="text-[10px] uppercase font-medium mb-1" style={{ color: 'var(--admin-text3)', letterSpacing: '0.5px' }}>
                      Horas
                    </p>
                    <p className="text-[13px] font-medium" style={{ color: 'var(--admin-text)' }}>
                      {formatHorasDecimales(Number(asesoria.horas) || horasDeAudios(asesoria.audios))}
                    </p>
                  </div>
                </div>

                {/* Objetivo */}
                {asesoria.objetivo && (
                  <div className="mb-4">
                    <p className="text-[10px] uppercase font-medium mb-1" style={{ color: 'var(--admin-text3)', letterSpacing: '0.5px' }}>
                      Objetivo
                    </p>
                    <p className="text-[13px]" style={{ color: 'var(--admin-text2)' }}>
                      {asesoria.objetivo}
                    </p>
                  </div>
                )}

                {asesoria.resumenPorNtc && asesoria.resumenPorNtc.length > 0 && (
                  <div className="mb-5">
                    <div className="text-[10px] uppercase font-medium mb-2" style={{ color: 'var(--admin-text3)', letterSpacing: '0.5px' }}>
                      Evaluación por norma
                    </div>
                    <div className="admin-card p-0 overflow-hidden">
                      <NtcResumenTable rows={asesoria.resumenPorNtc} />
                    </div>
                  </div>
                )}

                {/* Recomendaciones collapsible */}
                {asesoria.recomendaciones && (
                  <div>
                    <p className="text-[10px] uppercase font-medium mb-2" style={{ color: 'var(--admin-text3)', letterSpacing: '0.5px' }}>
                      Recomendaciones / Historial de Rechazos
                    </p>
                    <div
                      className="rounded-lg p-3"
                      style={{ background: 'var(--admin-surface2)', border: '1px solid var(--admin-border)' }}
                    >
                      {renderRecomendacionesExpandible(asesoria.recomendaciones)}
                    </div>
                  </div>
                )}
              </div>

              {/* Banner EN_REVISION */}
              {enRevision && (
                <div
                  className="rounded-xl p-4 flex items-center justify-between mb-4"
                  style={{ background: '#fdf3e0', border: '1px solid #f0dba0' }}
                >
                  <div>
                    <p className="text-[13px] font-medium" style={{ color: '#b56b00' }}>
                      Asesoría en revisión — no se puede modificar
                    </p>
                    <p className="text-[11px] mt-0.5" style={{ color: '#b56b00' }}>
                      Esperando aprobación del administrador
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCancelarModal(true)}
                    className="admin-btn"
                    style={{ fontSize: '11px', background: 'var(--admin-red)', color: '#fff', borderColor: 'var(--admin-red)' }}
                  >
                    Revertir asesoría
                  </button>
                </div>
              )}

              {/* Quick actions */}
              <div className="flex flex-col gap-2 mb-6">
                <div className="flex flex-wrap gap-2">
                  <Link to={`/empresa/${nit}/asesoria/${asesoriaId}/evaluacion`}>
                    <button type="button" className="admin-btn primary">
                      <ClipboardList className="w-3.5 h-3.5" />
                      {puedeEditar ? 'Evaluar Requisitos' : 'Ver Evaluación'}
                    </button>
                  </Link>

                  <Link to={`/empresa/${nit}/asesoria/${asesoriaId}/reuniones`}>
                    <button type="button" className="admin-btn">
                      <Calendar className="w-3.5 h-3.5" /> Reuniones ({reuniones})
                    </button>
                  </Link>

                  {puedeEditar && hasFocus && (
                    <button
                      type="button"
                      disabled={!isFormUrlReady}
                      onClick={() => {
                        if (data?.formUrl) {
                          window.open(data.formUrl, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      className="admin-btn disabled:opacity-40"
                    >
                      <Upload className="w-3.5 h-3.5" /> Subir Audio
                    </button>
                  )}

                  {puedeEditar && data?.source === 'DB' && (
                    <button
                      type="button"
                      onClick={() => {
                        setAccessEmail('');
                        setIsEmailModalOpen(true);
                      }}
                      disabled={isRequestingFormAccess || !isFormUrlReady}
                      className="admin-btn disabled:opacity-50"
                      style={{ fontSize: '11px', color: 'var(--admin-blue)' }}
                    >
                      {isRequestingFormAccess ? 'Solicitando acceso...' : 'Obtener acceso al formulario'}
                    </button>
                  )}
                </div>

                {puedeEditar && hasFocus && (
                  <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-green-200 bg-green-50 text-[12px] text-green-800">
                    <div className="flex items-start gap-1.5 min-w-0">
                      <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-green-600" />
                      <div>
                        <span className="font-semibold text-green-900">Esta asesoría tiene el foco de audio activo</span>
                        <p className="text-green-700 mt-0.5">
                          Cualquier audio que subas a través del formulario o de la plataforma se asociará a esta asesoría de forma
                          automática.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowFocoExpl(true)}
                      className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold hover:underline"
                      style={{ color: 'var(--admin-blue)' }}
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                      ¿Qué significa?
                    </button>
                  </div>
                )}

                {puedeEditar && !hasFocus && (
                  <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50 text-[12px] text-amber-800">
                    <div className="flex items-start gap-1.5 min-w-0">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600" />
                      <div>
                        <span className="font-semibold text-amber-950">Sin foco de audio activo</span>
                        {focusId && focusLabel && (
                          <span className="text-amber-900 block mt-0.5 font-medium">
                            La{' '}
                            <Link to={`/empresa/${nit}/asesoria/${focusId}`} className="underline font-semibold hover:opacity-80">
                              {focusIsAnterior ? 'asesoría anterior' : focusIsSiguiente ? 'asesoría siguiente' : `asesoría ${focusLabel}`}
                            </Link>{' '}
                            tiene el foco actual.
                          </span>
                        )}
                        <p className="text-amber-700 mt-0.5">Si subes un audio, se registrará en la otra asesoría y no aquí.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => setShowFocoExpl(true)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold hover:underline mr-1"
                        style={{ color: 'var(--admin-blue)' }}
                      >
                        <HelpCircle className="w-3.5 h-3.5" />
                        ¿Qué es?
                      </button>
                      <button
                        type="button"
                        disabled={settingFoco}
                        onClick={handleSetFoco}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors disabled:opacity-50 hover:opacity-90"
                        style={{ background: 'var(--admin-blue)', border: '1px solid var(--admin-blue)', color: '#fff' }}
                      >
                        {settingFoco ? 'Actualizando…' : 'Tomar foco'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Reuniones */}
              <div className="admin-card mb-5">
                <div className="admin-card-header" style={{ justifyContent: 'space-between' }}>
                  <span className="text-[13px] font-semibold" style={{ color: 'var(--admin-text)' }}>
                    Reuniones
                  </span>
                  <Link to={`/empresa/${nit}/asesoria/${asesoriaId}/reuniones`}>
                    <button type="button" className="admin-btn" style={{ fontSize: '11px', padding: '4px 10px' }}>
                      {puedeEditar ? '+ Nueva' : 'Ver'}
                    </button>
                  </Link>
                </div>

                {asesoria.reuniones.length === 0 ? (
                  <div className="py-8 text-center text-[13px]" style={{ color: 'var(--admin-text3)' }}>
                    No hay reuniones agendadas
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Hora Inicio</th>
                          <th>Hora Fin</th>
                          <th>Motivo</th>
                          <th>Estado</th>
                          <th style={{ textAlign: 'right' }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {asesoria.reuniones.map((reunion) => {
                          const fechaInicio = reunion.fechaInicio ? new Date(reunion.fechaInicio) : null;
                          const fechaFin = reunion.fechaFin ? new Date(reunion.fechaFin) : null;

                          const formatFecha = (date: Date | null) => {
                            if (!date || Number.isNaN(date.getTime())) return 'N/A';
                            return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
                          };

                          const formatHora = (date: Date | null) => {
                            if (!date || Number.isNaN(date.getTime())) return 'N/A';
                            return date.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
                          };

                          return (
                            <tr key={reunion.reunionId}>
                              <td style={{ color: 'var(--admin-text)' }}>{formatFecha(fechaInicio)}</td>
                              <td style={{ color: 'var(--admin-text2)' }}>{formatHora(fechaInicio)}</td>
                              <td style={{ color: 'var(--admin-text2)' }}>{formatHora(fechaFin)}</td>
                              <td style={{ color: 'var(--admin-text2)' }}>{reunion.motivo || 'Sin motivo especificado'}</td>
                              <td>
                                <span className="admin-chip" style={getReunionChipStyle(reunion.estado)}>
                                  {reunion.estado}
                                </span>
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <Link to={`/empresa/${nit}/asesoria/${asesoriaId}/reuniones`}>
                                  <button type="button" className="admin-btn" style={{ fontSize: '11px', padding: '4px 10px' }}>
                                    Detalle
                                  </button>
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Evidencias — solo lectura en Detalle (badge de estado, sin controles de acción). */}
              <div id="seccion-evidencias" className="admin-card mb-5" style={{ scrollMarginTop: '80px' }}>
                <div className="admin-card-header flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex gap-2 flex-wrap flex-col text">
                    <span className="text-[13px] font-semibold" style={{ color: 'var(--admin-text)' }}>
                      Evidencias ({evidenciasVigentes.length})
                    </span>
                    {bloqueaSubidaNueva && (
                      <span className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                        El administrador solo permite reemplazar las evidencias rechazadas en esta correccion
                      </span>
                    )}
                  </div>
                  {puedeSubirEvidencia && (
                    <button
                      type="button"
                      onClick={() => setIsEvidenceModalOpen(true)}
                      className="flex items-center gap-1.5 px-3 py-1 text-[11px] font-bold bg-[#1a1a1a] text-white rounded hover:bg-gray-800 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Subir Evidencia
                    </button>
                  )}
                </div>
                {evidenciasVigentes.length === 0 ? (
                  <div className="py-8 text-center text-[13px]" style={{ color: 'var(--admin-text3)' }}>
                    No hay evidencias disponibles
                  </div>
                ) : (
                  <div>
                    {evidenciasVigentes.map((evidencia) => {
                      const rawEntries =
                        cadenasEvidencias
                          .find((c) => c.version.some((v) => v.evidenciaId === evidencia.evidenciaId))
                          ?.version.flatMap((v) => historialPorElemento.get(`EVIDENCIA:${v.evidenciaId}`) ?? []) ??
                        historialPorElemento.get(`EVIDENCIA:${evidencia.evidenciaId}`) ??
                        [];
                      const entries = [...rawEntries].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());

                      return (
                        <div
                          key={evidencia.evidenciaId}
                          className="flex items-center justify-between gap-2 px-4 py-2.5 text-[12px] border-b border-gray-100 last:border-b-0"
                        >
                          <a
                            href={evidencia.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 flex-1 min-w-0 hover:underline"
                            title="Abrir archivo"
                          >
                            <EstadoBadgeDetalle estado={evidencia.estado} entries={entries} />
                            <span className="truncate" style={{ color: 'var(--admin-text)' }}>
                              {evidencia.nombre}
                            </span>
                            {showSecretPanel && (
                              <span className="font-mono text-[10px] text-amber-700 bg-amber-50 px-1 py-0.5 rounded border border-amber-200 select-all shrink-0 font-semibold">
                                #{evidencia.evidenciaId}
                              </span>
                            )}
                            <ExternalLink className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                          </a>
                          {(puedeEditar || showSecretPanel) && evidencia.estado !== 'ELIMINADO' && evidencia.estado !== 'APROBADO' && (
                            <button
                              type="button"
                              onClick={() => handleEliminarEvidencia(evidencia.evidenciaId, evidencia.reemplazaEvidenciaId != null)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors cursor-pointer shrink-0"
                              title="Eliminar evidencia"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Audios — collapsible. Badge de estado agregado (§6.6.3); "vincular a rechazado" se
                  removió de Detalle (queda solo en Correcciones necesarias); el resto de la gestión
                  general del audio (fecha, acceso, eliminar) no es una acción de corrección y se conserva. */}
              <div id="seccion-audios" className="admin-card mb-5" style={{ scrollMarginTop: '80px' }}>
                <button
                  type="button"
                  className="admin-card-header w-full text-left"
                  style={{ justifyContent: 'space-between', cursor: 'pointer', background: 'none', border: 'none' }}
                  onClick={() => setAudiosExpanded((v) => !v)}
                >
                  <span className="text-[13px] font-semibold" style={{ color: 'var(--admin-text)' }}>
                    Audios ({audioItems.length})
                  </span>
                  {audiosExpanded ? (
                    <ChevronUp className="w-4 h-4" style={{ color: 'var(--admin-text3)' }} />
                  ) : (
                    <ChevronDown className="w-4 h-4" style={{ color: 'var(--admin-text3)' }} />
                  )}
                </button>

                {audiosExpanded &&
                  (audioItems.length === 0 ? (
                    <div className="py-8 text-center text-[13px]" style={{ color: 'var(--admin-text3)' }}>
                      No hay audios registrados
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Estado</th>
                            <th>Fecha</th>
                            <th>Duración</th>
                            <th style={{ textAlign: 'right' }}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {audioItems.map((audio) => {
                            const audioKey = audio.audioId?.toString() ?? audio.googleId ?? '';
                            const audioLink = `https://drive.google.com/file/d/${audio.googleId}/view`;
                            const isEditingThis = editingAudioId === audioKey;

                            const handleStartEdit = () => {
                              setEditingAudioId(audioKey);
                              setEditingAudioFecha(formatDateTimeLocal(audio.fechaGrabacion).split('T')[0]);
                            };

                            const handleCancelEdit = () => {
                              setEditingAudioId(null);
                              setEditingAudioFecha('');
                            };

                            const handleSaveAudioFecha = async () => {
                              if (!audio.audioId) return;
                              if (!editingAudioFecha) return;
                              const newDate = new Date(`${editingAudioFecha}T00:00:00-05:00`);
                              if (Number.isNaN(newDate.getTime())) return;
                              try {
                                await updateAudioFecha(audio.audioId, newDate);
                                setEditingAudioId(null);
                                setEditingAudioFecha('');
                              } catch {
                                // error is handled in the hook; stays in edit mode
                              }
                            };

                            const rawAudioEntries = audio.audioId
                              ? (cadenasAudios
                                  .find((c) => c.version.some((v) => v.audioId === audio.audioId))
                                  ?.version.flatMap((v) => historialPorElemento.get(`AUDIO:${v.audioId}`) ?? []) ??
                                historialPorElemento.get(`AUDIO:${audio.audioId}`) ??
                                [])
                              : [];
                            const audioEntries = [...rawAudioEntries].sort(
                              (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
                            );

                            const audioEstaAprobado = audio.estado === 'APROBADO' || estaAprobado(audioEntries);
                            const audioEstaRechazado = tieneRechazoActivo(audioEntries);

                            return (
                              <tr key={audioKey} style={audio.estado === 'RECHAZADO' ? { background: '#fdf0ee' } : undefined}>
                                <td>
                                  <EstadoBadgeDetalle estado={audio.estado} entries={audioEntries} />
                                </td>
                                <td style={{ color: 'var(--admin-text)' }}>
                                  {isEditingThis ? (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="date"
                                        value={editingAudioFecha}
                                        onChange={(e) => setEditingAudioFecha(e.target.value)}
                                        className="px-2 py-1 border rounded-md text-[12px] outline-none focus:ring-2 focus:ring-[#1a1a1a]"
                                        style={{ borderColor: 'var(--admin-border2)' }}
                                        disabled={isAudioFechaUpdating}
                                      />
                                      <button
                                        type="button"
                                        onClick={handleSaveAudioFecha}
                                        disabled={isAudioFechaUpdating}
                                        className="admin-btn primary disabled:opacity-50"
                                        style={{ fontSize: '11px', padding: '4px 10px' }}
                                      >
                                        {isAudioFechaUpdating ? 'Guardando...' : 'Guardar'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={handleCancelEdit}
                                        disabled={isAudioFechaUpdating}
                                        className="admin-btn disabled:opacity-50"
                                        style={{ fontSize: '11px', padding: '4px 10px' }}
                                      >
                                        Cancelar
                                      </button>
                                    </div>
                                  ) : (
                                    <span>{formatAudioDate(audio.fechaGrabacion)}</span>
                                  )}
                                </td>
                                <td style={{ color: 'var(--admin-text2)' }}>{formatDuration(audio.duracion ?? 0)}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <div className="flex items-center justify-end gap-3">
                                    <a
                                      className="flex items-center gap-1 text-[12px] font-medium"
                                      style={{ color: 'var(--admin-blue)' }}
                                      href={audioLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <ExternalLink className="w-3.5 h-3.5" />
                                      Abrir
                                    </a>
                                    {(puedeEditar || showSecretPanel) && audio.audioId && (
                                      <div className="relative" ref={openMenuId === audioKey ? menuRef : null}>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            if (openMenuId === audioKey) {
                                              setOpenMenuId(null);
                                            } else {
                                              const rect = e.currentTarget.getBoundingClientRect();
                                              setMenuPosition({ top: rect.bottom + 4, left: rect.right - 192 });
                                              setOpenMenuId(audioKey);
                                            }
                                          }}
                                          className="p-1.5 rounded-md transition-colors"
                                          style={{ color: 'var(--admin-text3)' }}
                                        >
                                          <MoreVertical className="w-4 h-4" />
                                        </button>
                                        {openMenuId === audioKey && (
                                          <>
                                            <div className="fixed inset-0 z-40" onClick={() => setOpenMenuId(null)} />
                                            <div
                                              className="fixed w-48 rounded-lg shadow-lg z-50 py-1"
                                              style={{
                                                top: menuPosition.top,
                                                left: menuPosition.left,
                                                background: 'var(--admin-surface)',
                                                border: '1px solid var(--admin-border)',
                                              }}
                                            >
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setOpenMenuId(null);
                                                  handleStartEdit();
                                                }}
                                                className="w-full text-left px-4 py-2 text-[12px] flex items-center gap-2 hover:bg-[#f2f1ee] transition-colors"
                                                style={{ color: 'var(--admin-text)' }}
                                              >
                                                <Pencil className="w-3.5 h-3.5" />
                                                Editar fecha
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setOpenMenuId(null);
                                                  requestAccess(audio.audioId!);
                                                }}
                                                disabled={isRequesting}
                                                className="w-full text-left px-4 py-2 text-[12px] flex items-center gap-2 hover:bg-[#f2f1ee] transition-colors disabled:opacity-50"
                                                style={{ color: 'var(--admin-text)' }}
                                              >
                                                <Key className="w-3.5 h-3.5" />
                                                Obtener acceso
                                              </button>
                                              {!audioEstaAprobado && (
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    setOpenMenuId(null);
                                                    setDeletingAudioId(audio.audioId!);
                                                    setIsDeleteModalOpen(true);
                                                  }}
                                                  className="w-full text-left px-4 py-2 text-[12px] flex items-center gap-2 hover:bg-[#fdf0ee] transition-colors"
                                                  style={{ color: 'var(--admin-red)' }}
                                                >
                                                  <Trash2 className="w-3.5 h-3.5" />
                                                  Eliminar
                                                </button>
                                              )}
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
              </div>

              {/* Sticky footer */}
              {(estadoAsesoria === 'EN_CURSO' || estadoAsesoria === 'REVERTIDA' || estadoAsesoria === 'REQUIERE_CORRECCIONES') && (
                <div
                  className="sticky bottom-0 mt-6 py-3 px-4 rounded-xl border flex items-center justify-between gap-3"
                  style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)' }}
                >
                  <div className="flex-1 min-w-0">
                    {motivoEnviarRevision && (
                      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 text-left">
                        <p className="flex items-start gap-1 font-medium">
                          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600" />
                          <span>{motivoEnviarRevision}</span>
                        </p>
                        {bloqueoCorrecciones && (
                          <ul className="mt-1 ml-5 list-disc space-y-0.5">
                            {correccionesPendientes.map((el) => (
                              <li key={`${el.elementoTipo}:${el.elementoId}`}>{el.elementoNombre}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleGoToFinalizar}
                    disabled={disabledEnviarRevision}
                    className="admin-btn primary disabled:opacity-50 shrink-0"
                    title={motivoEnviarRevision}
                  >
                    {isCheckingAudioStatus ? 'Validando audios...' : 'Enviar a Revisión'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal: revertir asesoría */}
      <Modal isOpen={showCancelarModal} onClose={() => setShowCancelarModal(false)} title="Revertir asesoría">
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg p-4" style={{ background: 'var(--admin-red-bg)', border: '1px solid #fca5a5' }}>
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--admin-red)' }} />
            <div>
              <p className="font-medium text-[13px]" style={{ color: 'var(--admin-red)' }}>
                Esta acción es irreversible
              </p>
              <p className="text-[12px] mt-1" style={{ color: 'var(--admin-red)' }}>
                La asesoría quedará en estado Revertida. Podrás seguir editando las evaluaciones y volver a enviarla a revisión cuando esté
                lista.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowCancelarModal(false)}
              disabled={isRevertiendo}
              className="admin-btn disabled:opacity-50"
            >
              Volver
            </button>
            <button
              type="button"
              onClick={handleRevertir}
              disabled={isRevertiendo}
              className="admin-btn disabled:opacity-50"
              style={{ background: 'var(--admin-red)', color: '#fff', borderColor: 'var(--admin-red)' }}
            >
              {isRevertiendo ? 'Revirtiendo...' : 'Sí, revertir'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal: audios en procesamiento */}
      <Modal
        isOpen={showAudioProcessingWarningModal}
        onClose={() => setShowAudioProcessingWarningModal(false)}
        title="Audios en procesamiento"
      >
        <div className="space-y-4">
          <div
            className="flex items-start gap-3 rounded-lg p-4"
            style={{ background: 'var(--admin-amber-bg)', border: '1px solid #f0dba0' }}
          >
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--admin-amber)' }} />
            <div>
              <p className="font-medium text-[13px]" style={{ color: 'var(--admin-amber)' }}>
                Se detectaron audios en procesamiento
              </p>
              <p className="text-[12px] mt-1" style={{ color: 'var(--admin-amber)' }}>
                Hay {audioProcessingStatus?.totalJobsEnProcesamiento ?? 0} job(s) de audio en estado pendiente o en progreso. Puedes esperar
                a que finalicen o continuar de todos modos.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowAudioProcessingWarningModal(false)}
              disabled={isCheckingAudioStatus}
              className="admin-btn disabled:opacity-50"
            >
              Esperar
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAudioProcessingWarningModal(false);
                navigate(`/empresa/${nit}/asesoria/${asesoriaId}/finalizar`);
              }}
              disabled={isCheckingAudioStatus}
              className="admin-btn disabled:opacity-50"
              style={{ background: 'var(--admin-red)', color: '#fff', borderColor: 'var(--admin-red)' }}
            >
              Continuar
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal: error de validación de audios */}
      <Modal
        isOpen={Boolean(audioStatusValidationError)}
        onClose={() => setAudioStatusValidationError(null)}
        title="No se pudo validar audios"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg p-4" style={{ background: 'var(--admin-red-bg)', border: '1px solid #f5c6c2' }}>
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--admin-red)' }} />
            <p className="text-[13px]" style={{ color: 'var(--admin-red)' }}>
              {audioStatusValidationError}
            </p>
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={() => setAudioStatusValidationError(null)} className="admin-btn primary">
              Entendido
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal: eliminar audio */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => !isDeleting && setIsDeleteModalOpen(false)} title="Eliminar Audio">
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg p-4" style={{ background: 'var(--admin-red-bg)', border: '1px solid #f5c6c2' }}>
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--admin-red)' }} />
            <div>
              <p className="font-medium text-[13px]" style={{ color: 'var(--admin-red)' }}>
                ¿Estás seguro de eliminar este audio?
              </p>
              <p className="text-[12px] mt-1" style={{ color: 'var(--admin-red)' }}>
                Esta acción no se puede deshacer. Se eliminará el audio tanto del sistema como de Google Drive, y se restará el tiempo de la
                asesoría.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
              className="admin-btn disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => deletingAudioId && deleteAudio(deletingAudioId)}
              disabled={isDeleting}
              className="admin-btn disabled:opacity-50"
              style={{ background: 'var(--admin-red)', color: '#fff', borderColor: 'var(--admin-red)' }}
            >
              {isDeleting ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal: acceso al audio */}
      <Modal isOpen={Boolean(accessSuccess || accessError)} onClose={clearAccessStatus} title="Acceso al Audio">
        <div className="space-y-4">
          <div
            className="flex items-start gap-3 rounded-lg p-4"
            style={
              accessError
                ? { background: 'var(--admin-red-bg)', border: '1px solid #f5c6c2' }
                : { background: 'var(--admin-green-bg)', border: '1px solid #b7dfc6' }
            }
          >
            {accessError ? (
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--admin-red)' }} />
            ) : (
              <Key className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--admin-green)' }} />
            )}
            <p className="text-[13px] font-medium" style={{ color: accessError ? 'var(--admin-red)' : 'var(--admin-green)' }}>
              {accessError || accessSuccess}
            </p>
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={clearAccessStatus} className="admin-btn primary">
              Entendido
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal: acceso al formulario */}
      <Modal isOpen={Boolean(formAccessSuccess || formAccessError)} onClose={clearFormAccessStatus} title="Acceso al Formulario">
        <div className="space-y-4">
          <div
            className="flex items-start gap-3 rounded-lg p-4"
            style={
              formAccessError
                ? { background: 'var(--admin-red-bg)', border: '1px solid #f5c6c2' }
                : { background: 'var(--admin-green-bg)', border: '1px solid #b7dfc6' }
            }
          >
            {formAccessError ? (
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--admin-red)' }} />
            ) : (
              <Key className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--admin-green)' }} />
            )}
            <p className="text-[13px] font-medium" style={{ color: formAccessError ? 'var(--admin-red)' : 'var(--admin-green)' }}>
              {formAccessError || formAccessSuccess}
            </p>
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={clearFormAccessStatus} className="admin-btn primary">
              Entendido
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal: solicitar acceso al formulario */}
      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => !isRequestingFormAccess && setIsEmailModalOpen(false)}
        title="Solicitar acceso al formulario"
      >
        <div className="space-y-4">
          <p className="text-[13px]" style={{ color: 'var(--admin-text2)' }}>
            Puedes solicitar acceso con tu correo configurado en la plataforma, o ingresar un correo diferente (por ejemplo, si prefieres
            usar otra cuenta de Google).
          </p>
          <Input
            label="Correo electrónico (Opcional)"
            type="email"
            placeholder={user?.email || 'tu correo de asesor'}
            value={accessEmail}
            onChange={(e) => setAccessEmail(e.target.value)}
            disabled={isRequestingFormAccess}
          />
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={() => setIsEmailModalOpen(false)}
              disabled={isRequestingFormAccess}
              className="admin-btn disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={async () => {
                await requestFormAccess(accessEmail.trim() || undefined);
                setIsEmailModalOpen(false);
              }}
              disabled={isRequestingFormAccess}
              className="admin-btn primary disabled:opacity-50"
            >
              {isRequestingFormAccess ? 'Solicitando...' : 'Solicitar Acceso'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal: editar asesoría */}
      <Modal isOpen={isEditOpen} onClose={closeEditModal} title="Editar asesoría" size="md">
        <div className="space-y-4">
          <Textarea
            label="Objetivo"
            value={objetivoValue}
            onChange={(event) => setObjetivoValue(event.target.value)}
            placeholder="Describe el objetivo de la asesoría"
            rows={4}
          />
          <Input
            label="Fecha de inicio"
            type="datetime-local"
            value={fechaInicioValue}
            onChange={(event) => setFechaInicioValue(event.target.value)}
            required
          />
          {saveError && (
            <p className="text-[12px]" style={{ color: 'var(--admin-red)' }}>
              {saveError}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={closeEditModal} disabled={isSaving} className="admin-btn disabled:opacity-50">
              Cancelar
            </button>
            <button type="button" onClick={handleSaveChanges} disabled={isSaving} className="admin-btn primary disabled:opacity-50">
              {isSaving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </Modal>

      {puedeSubirEvidencia && (
        <QuickActionsSpeedDial
          actions={[
            {
              icon: <FileUp className="w-5 h-5" />,
              label: 'Subir Evidencia',
              onClick: () => setIsEvidenceModalOpen(true),
            },
          ]}
        />
      )}

      <NuevaEvidenciaModal
        isOpen={isEvidenceModalOpen}
        onClose={() => setIsEvidenceModalOpen(false)}
        nit={nit!}
        asesoriaId={Number(asesoriaId)}
        onSuccess={refetch}
        disabled={!puedeSubirEvidencia}
        disabledMessage={
          bloqueaSubidaNueva
            ? 'El administrador habilito solo el reemplazo de evidencias rechazadas para esta correccion.'
            : ASESORIA_READONLY_MESSAGE[estadoAsesoria]
        }
      />
      <Modal isOpen={showFocoExpl} onClose={() => setShowFocoExpl(false)} title="Foco de la Asesoría" size="md">
        <div className="space-y-4 text-[13px] leading-relaxed text-[#444]">
          <div className="p-3.5 bg-blue-50 border border-blue-100 rounded-xl flex items-start gap-3">
            <CheckCircle className="w-5 h-5 shrink-0 mt-0.5 text-blue-600" />
            <div>
              <p className="font-semibold text-blue-900 text-[14px]">El contexto de trabajo activo</p>
              <p className="text-blue-700 mt-1 text-[13px]">
                La plataforma utiliza el "foco" para identificar qué asesoría de la empresa está activa y debe recibir las actualizaciones
                en este momento.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-bold text-[#1a1a1a] text-[13.5px] uppercase tracking-wider">¿En qué influye el foco activo?</h4>
            <div className="grid gap-3">
              <div className="p-3 bg-[#faf9f6] border border-[#ede9e3] rounded-lg">
                <p className="font-semibold text-[#1a1a1a] flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                  Subida y asociación de audios
                </p>
                <p className="text-[#666] mt-1 text-[12.5px] pl-3">
                  Cualquier audio que se suba desde el formulario de Google o se procese en la plataforma se asigna y suma horas de forma
                  automática a la asesoría que tenga el foco activo.
                </p>
              </div>

              <div className="p-3 bg-[#faf9f6] border border-[#ede9e3] rounded-lg">
                <p className="font-semibold text-[#1a1a1a] flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                  Matriz de Requisitos y Evaluación
                </p>
                <p className="text-[#666] mt-1 text-[12.5px] pl-3">
                  Los avances en las evaluaciones y variables de los indicadores de la empresa se enlazan dinámicamente con esta asesoría.
                </p>
              </div>
            </div>
          </div>

          <div className="pt-2 text-[12px] text-[#888]">
            <p>
              <strong>Nota:</strong> Solo una asesoría por empresa puede estar en foco a la vez. Si vas a trabajar en una asesoría
              diferente, recuerda presionar el botón "Tomar foco" para asegurar que los registros se guarden en el lugar correcto.
            </p>
          </div>

          <div className="flex justify-end pt-2">
            <button type="button" onClick={() => setShowFocoExpl(false)} className="admin-btn primary">
              Entendido
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
